import { Link } from 'react-router-dom';
import { useState } from 'react';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { faqs } from '@/mocks/faqs';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

function FAQSection() {
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  return (
    <section className="py-16 md:py-20 bg-background-50">
      <div className="max-w-3xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
            常见问题
          </h2>
          <p className="text-foreground-600">关于福利导航，你想知道的都在这里</p>
        </div>
        <div className="flex flex-col gap-3">
          {faqs.map((faq, idx) => (
            <div
              key={idx}
              className="border border-background-200/70 rounded-lg overflow-hidden"
            >
              <button
                className="w-full flex items-center justify-between px-5 py-4 text-left text-sm font-medium text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer"
                onClick={() => setOpenIdx(openIdx === idx ? null : idx)}
              >
                <span>{faq.question}</span>
                <span className={`w-5 h-5 flex items-center justify-center transition-transform duration-200 ${openIdx === idx ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-foreground-500"></i>
                </span>
              </button>
              {openIdx === idx && (
                <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                  {faq.answer}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function HomePage() {
  const featuredArticles = articles.slice(0, 6);

  return (
    <>
      <Navbar />

      {/* Hero */}
      <section className="relative w-full min-h-[600px] md:min-h-[700px] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(https://readdy.ai/api/search-image?query=Warm%20cozy%20Chinese%20lifestyle%20interior%20scene%20with%20gift%20boxes%20and%20warm%20golden%20lighting%2C%20abstract%20geometric%20pattern%20soft%20background%2C%20modern%20minimalist%20with%20traditional%20touches%2C%20feeling%20of%20abundance%20and%20generosity%2C%20editorial%20quality%2C%20warm%20tones%20of%20amber%20and%20cream&width=1600&height=900&seq=hero-welfare-01&orientation=landscape)`,
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/25 to-black/55"></div>
        <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center">
          <span className="inline-block px-4 py-1.5 rounded-full bg-primary-500/90 text-background-50 text-xs font-medium mb-6 tracking-wide">
            一站式福利资源聚合平台
          </span>
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 mb-6 leading-tight">
            发现最新福利
            <br />
            <span className="text-accent-400">让每一分钱都花得值</span>
          </h1>
          <p className="text-background-200/90 text-base md:text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
            精准聚合各类福利资源，涵盖优惠券、会员权益、生活补贴、职场福利等多维度内容，一站式省时省钱。
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/category/youhuiquan"
              className="px-6 py-3 bg-primary-500 text-background-50 font-medium rounded-full hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
            >
              开始探索福利
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
            <Link
              to="/about"
              className="px-6 py-3 border border-background-50/30 text-background-50 font-medium rounded-full hover:bg-background-50/10 transition-all cursor-pointer whitespace-nowrap"
            >
              了解更多
            </Link>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              福利分类
            </h2>
            <p className="text-foreground-600 max-w-xl mx-auto">
              八大核心分类，精准定位你需要的福利资源
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="group relative bg-background-50 border border-background-200/70 rounded-xl p-5 md:p-6 hover:border-accent-300 transition-all cursor-pointer"
              >
                <span className={`w-11 h-11 md:w-12 md:h-12 rounded-xl bg-${cat.color}-100 flex items-center justify-center mb-4 group-hover:bg-${cat.color}-200 transition-colors`}>
                  <i className={`${cat.icon} text-xl text-${cat.color}-600`}></i>
                </span>
                <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-950 mb-2">
                  {cat.name}
                </h3>
                <p className="text-xs md:text-sm text-foreground-500 leading-relaxed line-clamp-2">
                  {cat.description}
                </p>
                <p className="mt-3 text-xs text-foreground-400">
                  {cat.articleCount} 篇福利
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-accent-500">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { value: '246', label: '福利文章' },
              { value: '8', label: '核心分类' },
              { value: '50万+', label: '月活用户' },
              { value: '98%', label: '好评率' },
            ].map((stat) => (
              <div key={stat.label}>
                <p className="font-heading text-2xl md:text-3xl font-bold text-background-50">
                  {stat.value}
                </p>
                <p className="text-sm text-background-50/80 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Articles */}
      <section className="py-16 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
                精选福利
              </h2>
              <p className="text-foreground-600 text-sm">最新最热福利内容，每周持续更新</p>
            </div>
            <Link
              to="/category/youhuiquan"
              className="hidden sm:flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
            >
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {featuredArticles.map((article) => (
              <Link
                key={article.slug}
                to={`/article/${article.slug}`}
                className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={article.coverImage}
                    alt={article.title}
                    title={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-background-50/90 text-xs font-medium text-foreground-700">
                    {article.categoryName}
                  </span>
                </div>
                <div className="p-4 md:p-5">
                  <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2 mb-3">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center gap-3 text-xs text-foreground-400">
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line"></i> {article.publishDate}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line"></i> {article.readTime}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-8 text-center sm:hidden">
            <Link
              to="/category/youhuiquan"
              className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
            >
              查看全部福利 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      <FAQSection />

      {/* CTA */}
      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
            不想错过任何福利？
          </h2>
          <p className="text-foreground-600 mb-8">
            订阅福利导航周报，每周精选福利内容直达你的邮箱，省心省力。
          </p>
          <Link
            to="/about"
            className="inline-flex items-center px-6 py-3 bg-primary-500 text-background-50 font-medium rounded-full hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
          >
            了解更多 <i className="ri-arrow-right-line ml-2"></i>
          </Link>
        </div>
      </section>

      <Footer />
    </>
  );
}